import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../U3121';
import {
  useU3121CopyCustomerAddressToPtsHandlers,
  type U3121CopyCustomerAddressToPtsParams,
  type U3121CopyCustomerAddressToPtsResult,
} from '../../U3121UseCopyCustomerAddressToPtsHandlers';

type U3121CopyCustomerAddressLinkProps = {
  enabled: boolean;
  resolveParams: () => U3121CopyCustomerAddressToPtsParams;
  onCopied: (mapped: NonNullable<U3121CopyCustomerAddressToPtsResult>) => void;
};

/**
 * Hyperlien « Copier l’adresse du client » pour le titre du panneau Adresse et lieu (BL-0436).
 */
export default function U3121CopyCustomerAddressLink({
  enabled,
  resolveParams,
  onCopied,
}: U3121CopyCustomerAddressLinkProps) {
  const { t } = useTranslation();
  const { copyToPts } = useU3121CopyCustomerAddressToPtsHandlers();
  const [loading, setLoading] = useState(false);

  const canClick = enabled && !loading;

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canClick) return;
    setLoading(true);
    try {
      const mapped = await copyToPts(resolveParams());
      if (mapped) onCopied(mapped);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!canClick}
      aria-disabled={!canClick}
      className={`ml-2 text-xs font-normal underline ${
        canClick ? 'text-blue-600 hover:text-blue-800 cursor-pointer' : 'text-gray-400 cursor-not-allowed no-underline'
      }`}
    >
      {t('lnkCopyCustomerAddress', { ns: NS })}
    </button>
  );
}
